import { useMemo, useEffect, useRef } from 'preact/hooks'
import { Search, ExternalLink, RefreshCw } from 'lucide-preact'
import type { JSX } from 'preact'
import type { FileImportRowGridDto } from '@/types/campaignAnalytics'
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell'
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell'
import { PersonAvatar } from '@/components/common/Avatar'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import { useUserProfile } from '@/hooks/useUserProfile'

interface CampaignListContactsSectionProps {
  filtered: FileImportRowGridDto[]
  search: string
  setSearch: (val: string) => void
  country: string
  setCountry: (val: string) => void
  countryOptions: string[]
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onSelectContact?: (c: FileImportRowGridDto) => void
  onContactUpdated?: (updated: Partial<FileImportRowGridDto> & { Id: string }) => void
  onRefresh?: () => void | Promise<void>
  refreshing?: boolean
}

export const CampaignListContactsSection = ({
  filtered,
  search,
  setSearch,
  country,
  setCountry,
  countryOptions,
  clientCompanyId,
  workspaceId,
  campaignId,
  onSelectContact,
  onContactUpdated,
  onRefresh,
  refreshing,
}: CampaignListContactsSectionProps) => {
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Cleanup: abort all in-flight requests on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => controller.abort())
      abortControllersRef.current.clear()
    }
  }, [])

  // Define columns for DataGrid
  const columns: ColumnConfig<FileImportRowGridDto>[] = useMemo(() => [
    {
      key: 'Name',
      header: 'Name',
      sortable: true,
      accessor: (contact) => [contact.FirstName, contact.LastName].filter(Boolean).join(' ').toLowerCase(),
      render: (contact) => (
        <div className="flex items-center gap-3">
          <PersonAvatar
            firstName={contact.FirstName}
            lastName={contact.LastName}
            profilePicUrl={contact.ProfilePicUrl}
            size="md"
          />
          <div className="flex flex-col">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {[contact.FirstName, contact.LastName].filter(Boolean).join(' ') || '—'}
            </div>
            {contact.Email && <div className="text-xs text-slate-500 dark:text-slate-400">{contact.Email}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'Phone',
      header: 'Phone',
      sortable: true,
      accessor: (contact) => (contact.Phone || '').toLowerCase(),
      render: (contact) => (
        <PhoneEnrichmentCell
          key={`phone-${contact.Id}-${contact.Phone}-${contact.GlobalContactPhone}`}
          phone={contact.Phone}
          globalContactPhone={contact.GlobalContactPhone}
          phoneEnrichmentAvailableDate={contact.PhoneEnrichmentAvailableDate}
          id={contact.Id}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          canEdit={canEdit}
          context="fileImportRow"
          isLastRow={false}
          onPhoneUpdated={(updated) => {
            const finalUpdate = {
              Id: contact.Id,
              ...updated,
              Phone: updated.GlobalContactPhone || updated.Phone
            }
            onContactUpdated?.(finalUpdate)
          }}
        />
      ),
    },
    {
      key: 'Enrichment',
      header: 'Enrichment',
      sortable: false,
      hidden: 'hidden sm:table-cell',
      render: (contact) => (
        <EnrichmentStatusCell
          enrichmentStatus={contact.EnrichmentStatus}
          enrichedAt={contact.EnrichedAt}
          enrichmentAvailableDate={contact.EnrichmentAvailableDate}
          enrichmentAttemptCount={contact.EnrichmentAttemptCount || 0}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          id={contact.Id}
          context="fileImportRow"
          onEnrichmentUpdated={(updated) => {
            onContactUpdated?.({
              Id: contact.Id,
              ...updated
            })
          }}
        />
      ),
    },
    {
      key: 'Title',
      header: 'Title',
      sortable: true,
      accessor: (contact) => (contact.JobTitle || '').toLowerCase(),
      render: (contact) => contact.JobTitle || '—',
    },
    {
      key: 'Company',
      header: 'Company',
      sortable: true,
      accessor: (contact) => (contact.CompanyName || '').toLowerCase(),
      render: (contact) => {
        if (!contact.CompanyName) return '—'
        if (contact.CompanyLinkedInId) {
          return (
            <a
              href={`https://www.linkedin.com/company/${contact.CompanyLinkedInId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
              onClick={e => e.stopPropagation()}
            >
              {contact.CompanyName}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )
        }
        return contact.CompanyName
      },
    },
    {
      key: 'Country',
      header: 'Country',
      sortable: true,
      hidden: 'hidden md:table-cell',
      accessor: (contact) => (contact.Country || '').toLowerCase(),
      render: (contact) => contact.Country || '—',
    },
    {
      key: 'LinkedIn',
      header: 'LinkedIn',
      sortable: false,
      render: (contact) => {
        if (!contact.LinkedinProfileUrl) return '—'
        return (
          <a
            href={contact.LinkedinProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
            onClick={e => e.stopPropagation()}
          >
            View <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )
      },
    },
  ], [clientCompanyId, workspaceId, campaignId, canEdit, onContactUpdated])

  // Sort comparator for DataGrid
  const getSortComparator = (key: string) => {
    return (a: FileImportRowGridDto, b: FileImportRowGridDto) => {
      const column = columns.find(col => col.key === key)
      if (!column?.accessor) return 0

      const aValue = column.accessor(a)
      const bValue = column.accessor(b)

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue)
      }

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    }
  }

  return (
    <>
      {/* Filters Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 mb-6">
        <div className="flex items-end justify-between gap-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Filters</h3>
          {onRefresh && (
            <button
              onClick={() => onRefresh()}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              title="Refresh from server"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                value={search}
                onInput={(e: JSX.TargetedEvent<HTMLInputElement, Event>) => setSearch(e.currentTarget.value)}
                placeholder="Search contacts"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Country</label>
            <select value={country} onChange={(e: JSX.TargetedEvent<HTMLSelectElement, Event>) => setCountry(e.currentTarget.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white">
              <option value="">All</option>
              {countryOptions.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* DataGrid Section */}
      <div className="relative">
        {refreshing && (
          <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-slate-600 dark:text-slate-300">Refreshing...</span>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-800 dark:text-slate-200">Contacts</h3>
            <div className="text-xs text-slate-500 dark:text-slate-400">{filtered.length.toLocaleString()} results</div>
          </div>

          <DataGrid
            data={filtered}
            columns={columns}
            getRowKey={(contact) => contact.Id}
            onRowClick={(contact) => onSelectContact?.(contact)}
            getSortComparator={getSortComparator}
            defaultSortKey="Name"
            defaultSortDirection="asc"
            pagination={true}
            pageSize={10}
            showRowBorders={true}
            emptyMessage="No contacts found"
          />
        </div>
      </div>
    </>
  )
}
